import { ReactNode, useCallback, useContext, useEffect } from 'react';
import { MultiSelectionToggleGlobal } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGlobal';
import { FilterToolbar } from 'src/components/FilterToolbar';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { ViewModeSelector } from 'src/components/MainFilterBar';
import { useDiscoveryFilters } from 'src/components/MainFilterBar/useDiscoveryFilters';
import { Content } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Button } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useFilterToolbarQuickFilters } from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { NotificationModal } from 'src/navigations/Routes/OnSaleDiscovery/Notifications/NotificationModal';
import { MetricViewIcon } from 'src/svgs/MetricViewIcon';
import { IconsFill } from 'src/svgs/Viagogo';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { ON_SALE_EVENT_VIEW_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ActionOutboxEntityType,
  EventNotificationStatus,
  EventNotificationType,
  Feature,
  OnSaleEventQuery,
  OnSaleEventViewMode,
  UserSetting,
} from 'src/WebApiController';

import * as styles from '../../MainFilterBar/MainFilterBar.css';
import { OnSaleEventSearchBox } from '../OnSaleEventSearchBox';
import { useDefaultOnSaleEventFilter } from './hooks/useDefaultOnSaleEventFilter';

export const DISCOVERY_VIEW_MODE_TO_ICON: Record<string, ReactNode> = {
  [OnSaleEventViewMode.MetricView]: (
    <MetricViewIcon fill={IconsFill.currentColor} withHoverEffect />
  ),
};

export function OnSaleEventFilterBar() {
  const isMobile = useMatchMedia('mobile');
  const { filters } = useDiscoveryFilters();
  const { setModal, closeModal } = useContext(ModalContext);

  const hasOnSaleEventDetailsViewFeature = useUserHasFeature(
    Feature.OnSaleEventDetailsView
  );
  const hasEventDiscoveryNotificationsFeature = useUserHasFeature(
    Feature.EventDiscoveryNotifications
  );

  const {
    filterQuery: onSaleEventQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setInitialQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<OnSaleEventQuery>();

  const defaultQuickFilters = useDefaultOnSaleEventFilter({
    initialQuery,
  });

  useEffect(() => {
    setTempQuery(onSaleEventQuery);
  }, [onSaleEventQuery, setTempQuery]);

  const filterToolbarProps = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStateOnSaleEvent,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomOnSaleEvent,
    defaultQuickFilters,
    currentQuery: onSaleEventQuery,
    initialQuery,
    onSelect: ({ query }) => {
      setFilterQuery(query);
    },
  });

  const { appliedFilters } = useFiltersHelpers({
    filters,
    activeQuery: onSaleEventQuery,
    mandatoryFiltersToShow: ['onSaleDates'],
    initialQuery,
  });

  const onCreateAlertClick = useCallback(() => {
    setModal({
      children: (
        <NotificationModal
          isOpen={true}
          onClose={closeModal}
          notificationInfo={{
            id: EMPTY_GUID,
            name: '',
            filters: tempQuery ?? onSaleEventQuery, // we want to use the current query as the filter (onSaleEventQuery is only after they Applied)
            notificationType: EventNotificationType.Email,
            status: EventNotificationStatus.Active,
            userIdTargets: [],
            webhookTargets: [],
          }}
        />
      ),
      clickOutsideToClose: true,
      centered: true,
      size: 'sm',
    });
  }, [closeModal, onSaleEventQuery, setModal, tempQuery]);

  const filterToolbar = onSaleEventQuery && (
    <FilterToolbar
      {...filterToolbarProps}
      filterAppliedCounts={appliedFilters.length}
      filterAppliedIds={appliedFilters}
      onSubmitFilter={() => setFilterQuery(tempQuery)}
      filters={filters}
      resetTempQuery={resetTempQuery}
      tempQuery={tempQuery}
      showCustomFilterButton
      mandatoryFiltersToShow={['onSaleDates']}
      customFilterActions={
        hasEventDiscoveryNotificationsFeature && (
          <Button variant="textPlain" onClick={onCreateAlertClick}>
            <Content id={ContentId.CreateNotification} />
          </Button>
        )
      }
    />
  );

  return (
    <div className={styles.mainFilterBarDiv}>
      <DialogProvider>
        {isMobile ? (
          filterToolbar
        ) : (
          <>
            <div className={styles.dockLeftDiv}>
              <div className={styles.mainFilterBarLeftChild}>
                {filterToolbar}
              </div>
            </div>
            <div className={styles.dockRightDiv}>
              <div className={styles.mainFilterBarRightChild}>
                <ViewModeSelector
                  query={onSaleEventQuery}
                  entityType={ActionOutboxEntityType.SellerEvent}
                  initialQuery={initialQuery}
                  setFilterQuery={setFilterQuery}
                  setInitialQuery={setInitialQuery}
                  viewModeSettingsId={UserSetting.InventoryPageViewMode}
                  viewModeCidMap={ON_SALE_EVENT_VIEW_MODE_TO_CID}
                  viewModeIconMap={DISCOVERY_VIEW_MODE_TO_ICON}
                />
                {/* <MultiSelectionToggleGlobal /> */}
                <OnSaleEventSearchBox />
                {hasOnSaleEventDetailsViewFeature && (
                  <MultiSelectionToggleGlobal />
                )}
              </div>
            </div>
          </>
        )}
      </DialogProvider>
    </div>
  );
}
